// Feature-chat turn → the shape `MessageBubble` renders.
//
// WHY there is a mapper at all. v3's panel embedded `OrpheusMain` wholesale, so a feature-chat turn
// was rendered by the very same bubble as a full-page chat turn — that is where its timestamp, copy
// / edit / retry controls and follow-up chips came from. v4 keeps the two message MODELS apart
// (feature chat has no streaming, no threads, no answer payload), so reuse of the presentation costs
// one projection instead of one shared model. This is that projection, and it is deliberately
// one-way: nothing maps back.
//
// `MessageBubble` is the reuse boundary, NOT `MessageList`. The list is coupled to a real thread —
// it mounts `AnswerPanel` and a `ReportIssueModal` bound to `thread`, and a feature chat has no
// thread to report against, so wiring it there would render a control that posts nowhere.
import type { FeatureChatMessage } from '../models/featureChat.types'
import type { ChatMessage } from '../models/orpheus.types'

/**
 * A pending assistant turn maps to `streaming` so the bubble shows its own in-flight treatment;
 * everything else is `completed`. Feature chat has no stream, so no other state is reachable.
 */
function streamState(message: FeatureChatMessage): ChatMessage['streamState'] {
  return message.pending ? 'streaming' : 'completed'
}

/**
 * Project one turn. `turnId` reuses `localId` because the bubble only needs it to be stable per
 * turn — a feature chat has no server-side turn identity to preserve across a resume.
 *
 * The neutral fields below are not padding: `MessageBubble` reads them, and giving it a shape it
 * cannot narrow would push `undefined` checks into presentation. Feature chat produces none of
 * them — no answer payload, no recommendations, no response blocks, no suggested prompts.
 */
export function toChatMessage(message: FeatureChatMessage, index: number): ChatMessage {
  return {
    localId: message.localId,
    turnId: message.localId,
    messageId: null,
    role: message.role,
    kind: 'text',
    content: message.content,
    index,
    createdAt: message.createdAt,
    updatedAt: message.createdAt,
    streamState: streamState(message),
    answer: null,
    recommendations: [],
    recommendationData: null,
    responseBlocks: [],
    followups: message.followups,
    suggestedPrompts: [],
    channels: [],
    controllerParams: null,
    setupStep: message.setupStep ?? null,
    setupCard: message.setupCard ?? null,
    filePreview: message.filePreview ?? null,
    credits: message.credits ?? null,
    ...(message.keyError ? { keyError: message.keyError } : {}),
  }
}

/**
 * The transcript as bubbles. A FAILED assistant turn is dropped rather than projected: its content
 * is empty, so the bubble would render a blank answer, and the panel shows the failure through the
 * surfaces the credits contract requires (`FeatureDepletedBanner` for a 402, a rate-limit alert
 * carrying the server's `Retry-After`, a retry action otherwise) — none of which `MessageBubble`
 * knows about. v3 had no such distinction: it wrote the error into the bubble as plain text and had
 * no depleted or rate-limited surface at all.
 */
export function toChatMessages(messages: readonly FeatureChatMessage[]): ChatMessage[] {
  const bubbles: ChatMessage[] = []
  for (const message of messages) {
    if (message.error) continue
    bubbles.push(toChatMessage(message, bubbles.length))
  }
  return bubbles
}

/** The failed turn at the end of the transcript, if the last thing that happened was a failure. */
export function trailingFailure(
  messages: readonly FeatureChatMessage[],
): FeatureChatMessage | null {
  const last = messages.at(-1)
  return last?.error ? last : null
}
